import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react'

const enabled = import.meta.env.VITE_AUTH_ENABLED === 'true'
const apiBaseUrl = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

const STORAGE_KEY = 'auth.session'

// Perfil já resolvido pelo backend (tabela local `app_user_roles`) -- o
// frontend não decodifica JWT nem conhece o Keycloak, só consome isso.
export interface Profile {
  sub: string
  email: string
  name: string
  tenantId: string | null
  tenantName: string | null
  roles: string[]
  // Recuperação de senha: o backend marca isto quando emitiu uma senha temporária e o
  // usuário ainda não trocou -- ProtectedRoute obriga a troca antes de qualquer tela.
  mustChangePassword?: boolean
}

interface StoredSession {
  accessToken: string
  refreshToken?: string
  expiresAt: number
  profile: Profile
}

interface AuthResponse {
  access_token: string
  refresh_token?: string
  expires_in: number
  profile: Profile
}

function readSession(): StoredSession | null {
  const raw = window.sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredSession
  } catch {
    return null
  }
}

function writeSession(session: StoredSession | null) {
  if (session) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  else window.sessionStorage.removeItem(STORAGE_KEY)
}

// Login/refresh vão sempre pro próprio backend do email-server -- ele media a
// conversa com o Keycloak internamente, o browser nunca fala com o Keycloak.
async function authRequest(path: 'login' | 'refresh', body: Record<string, string>): Promise<StoredSession> {
  const response = await fetch(`${apiBaseUrl}/api/v1/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => null) as { detail?: string } | null
    throw new Error(detail?.detail ?? 'Usuário ou senha inválidos.')
  }
  const data = await response.json() as AuthResponse
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    profile: data.profile,
  }
}

interface AuthContextValue {
  loading: boolean
  authenticated: boolean
  token?: string
  profile: Profile
  isAdmin: boolean
  isSuperAdmin: boolean
  isAdminReadOnly: boolean
  isReseller: boolean
  mustChangePassword: boolean
  clearMustChangePassword: () => void
  login: (username: string, password: string, turnstileToken?: string | null) => Promise<void>
  logout: () => Promise<void>
}

const mockProfile: Profile = { sub: 'local-admin', email: 'admin@localhost', name: 'Administrador local', tenantId: null, tenantName: null, roles: ['ADMIN'], mustChangePassword: false }
const emptyProfile: Profile = { sub: '', email: '', name: '', tenantId: null, tenantName: null, roles: [], mustChangePassword: false }
const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<StoredSession | null>(null)
  const [loading, setLoading] = useState(enabled)

  const clearSession = useCallback(() => {
    writeSession(null)
    setSession(null)
  }, [])

  // Na montagem, recupera a sessão salva; se estiver vencida (ou perto
  // disso), tenta renovar antes de decidir que o usuário não está logado --
  // evita mandar de volta pro /login só porque a aba ficou aberta demais.
  useEffect(() => {
    if (!enabled) return
    let active = true
    const bootstrap = async () => {
      const stored = readSession()
      if (!stored) return
      if (stored.expiresAt - Date.now() > 5_000) {
        if (active) setSession(stored)
        return
      }
      if (!stored.refreshToken) {
        writeSession(null)
        return
      }
      try {
        const next = await authRequest('refresh', { refreshToken: stored.refreshToken })
        writeSession(next)
        if (active) setSession(next)
      } catch {
        writeSession(null)
      }
    }
    bootstrap().finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  // Qualquer chamada à API que volte 401 (sessão expirou no servidor antes
  // da renovação automática rodar, ex.: aba em segundo plano) dispara esse
  // evento em `lib/api.ts` -- encerra a sessão local, o que faz o
  // ProtectedRoute redirecionar pro /login sozinho.
  useEffect(() => {
    if (!enabled) return
    window.addEventListener('auth:session-expired', clearSession)
    return () => window.removeEventListener('auth:session-expired', clearSession)
  }, [clearSession])

  // Renova o token um pouco antes de expirar, sempre que a sessão muda.
  useEffect(() => {
    if (!enabled || !session?.refreshToken) return
    const delay = Math.max((session.expiresAt - Date.now()) * 0.8, 0)
    const timer = setTimeout(async () => {
      try {
        const next = await authRequest('refresh', { refreshToken: session.refreshToken! })
        writeSession(next)
        setSession(next)
      } catch {
        clearSession()
      }
    }, delay)
    return () => clearTimeout(timer)
  }, [session, clearSession])

  const login = useCallback(async (username: string, password: string, turnstileToken?: string | null) => {
    const next = await authRequest('login', turnstileToken ? { username, password, turnstileToken } : { username, password })
    writeSession(next)
    setSession(next)
  }, [])

  const logout = useCallback(async () => {
    if (enabled && session?.refreshToken) {
      try {
        await fetch(`${apiBaseUrl}/api/v1/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        })
      } catch {
        // revogação é best-effort -- a sessão local é limpa de qualquer forma
      }
    }
    clearSession()
  }, [session, clearSession])

  const profile = enabled ? (session?.profile ?? emptyProfile) : mockProfile
  const authenticated = !enabled || Boolean(session)

  const clearMustChangePassword = useCallback(() => {
    setSession((prev) => {
      if (!prev) return prev
      const next = { ...prev, profile: { ...prev.profile, mustChangePassword: false } }
      writeSession(next)
      return next
    })
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    authenticated,
    token: session?.accessToken,
    profile,
    isAdmin: !enabled || profile.roles.includes('ADMIN') || profile.roles.includes('SUPER_ADMIN'),
    isSuperAdmin: enabled && profile.roles.includes('SUPER_ADMIN'),
    isAdminReadOnly: enabled && profile.roles.includes('ADMIN_READONLY'),
    isReseller: enabled && profile.roles.includes('RESELLER'),
    mustChangePassword: enabled && Boolean(profile.mustChangePassword),
    clearMustChangePassword,
    login,
    logout,
  }), [loading, authenticated, session, profile, clearMustChangePassword, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth deve estar dentro de AuthProvider')
  return context
}
